import { getUserId } from "../../lib/effectiveAuthUser";
// Owns TiptapDocumentSB's right-bar card lists, favorites, pagination, and draft placeholder lifecycle.
import type React from "react";

import firebase from "../../Firebase.js";
import Card from "../../models/Card";
import {
  createRightBarCardsFromSnapshot,
  getLoadedExistingCardsState,
  getPrependedNewCardState,
  isCurrentDocumentSwitchVersion,
  markCardPageRequested,
  shouldSkipCardPageRequest,
} from "./helpers";
import { useStableCallback } from "./useStableCallback";

const db = firebase.firestore();

type CardContent = unknown;

type CardModel = {
  id: string | null;
  data: {
    content?: CardContent;
    [key: string]: unknown;
  };
  setFetched: (id: string) => void;
};

type CardConstructor = new (id: string | null, data: unknown, fetched: boolean) => CardModel;

const CardModelConstructor = Card as unknown as CardConstructor;

type CardPageCursor = {
  id: string;
};

type FirestoreCardDoc = CardPageCursor & {
  data: () => unknown;
};

type FirestoreCardSnapshot = {
  forEach: (callback: (doc: FirestoreCardDoc) => void) => void;
};

type RightBarCardError = {
  message?: string;
};

type RightBarCard = {
  loading: boolean;
  doneStreaming: boolean;
  hidden: boolean;
  model: CardModel;
  ready: boolean;
  params?: Record<string, unknown>;
  snapshot?: CardPageCursor;
  onRedo?: null;
  error?: RightBarCardError | null;
};

type RightBarCardListKey = "newCards" | "existingCards";

type FavoritesByCards = Record<string, unknown>;

type RightBarCardsState = {
  newCards: RightBarCard[];
  existingCards: RightBarCard[];
  rightTab: number;
  favorites: unknown[];
  favoritesByCards: FavoritesByCards;
  existingCardsLoaded: boolean;
};

type RightBarCardsStateUpdate = Partial<RightBarCardsState> | Record<string, unknown>;

type RightBarCardsStateUpdater = (previousState: RightBarCardsState) => RightBarCardsStateUpdate | null;

type SetMergedState = (stateUpdate: RightBarCardsStateUpdate | RightBarCardsStateUpdater, callback?: () => void) => void;

type FavoritesStore = {
  createFromCard: (card: CardModel, content: unknown) => Promise<unknown>;
  deleteFromCard: (card: CardModel, content: unknown) => Promise<unknown>;
  delete: (id: string) => Promise<unknown>;
  reorder: (from: number, to: number) => void;
  getSortedFavorites: () => unknown[];
  calculateFavoritesByCards: () => FavoritesByCards;
};

type UseRightBarCardsParams = {
  stateRef: React.MutableRefObject<RightBarCardsState>;
  setMergedState: SetMergedState;
  documentIdRef: React.MutableRefObject<string | null>;
  documentSwitchVersionRef: React.MutableRefObject<number>;
  cardQueriesRef: React.MutableRefObject<Record<string, boolean>>;
  favoritesStoreRef: React.MutableRefObject<FavoritesStore | null>;
  placeholderRafIdRef: React.MutableRefObject<number | null>;
  unCollapseRightBar: () => void;
  stride: number;
};

type DraftPlaceholderOptions = {
  prompt?: string;
  comparisonMode?: boolean;
  modelLabel?: string;
};

type DraftPlaceholderContentOptions = {
  index?: number;
};

type UseRightBarCardsReturn = {
  refreshCards: () => void;
  getCards: (limit: number, startAfter?: CardPageCursor | null, switchVersion?: number | null) => Promise<void>;
  prependNewCard: (card: RightBarCard) => void;
  markCardStreamSettled: (cardModel: CardModel | null | undefined, error?: RightBarCardError) => void;
  deleteCard: (id: string, keepCard?: boolean) => void;
  deleteCardItem: (id: string, removeCard: boolean) => void;
  onNextPage: () => void;
  onChangeTab: (tab: number) => void;
  onCardFavorite: (card: CardModel, content: unknown) => void;
  onCardUnfavorite: (card: CardModel, content: unknown) => void;
  onDeleteFavorite: (id: string) => void;
  onFavoriteReorder: (from: number, to: number) => void;
  addDraftPlaceholderCard: (options?: DraftPlaceholderOptions) => CardModel;
  appendDraftPlaceholderContent: (cardModel: CardModel | null | undefined, text: string, options?: DraftPlaceholderContentOptions) => void;
  linkDraftPlaceholderToCardId: (cardModel: CardModel | null | undefined, cardId: string | null | undefined) => void;
  finalizeDraftPlaceholder: (cardModel: CardModel | null | undefined) => void;
  removeDraftPlaceholderCard: (cardModel: CardModel | null | undefined) => void;
};

/**
 * Builds the card-management API used by TiptapDocumentSB while keeping state ownership in the parent component.
 */
export function useRightBarCards({
  stateRef,
  setMergedState,
  documentIdRef,
  documentSwitchVersionRef,
  cardQueriesRef,
  favoritesStoreRef,
  placeholderRafIdRef,
  unCollapseRightBar,
  stride,
}: UseRightBarCardsParams): UseRightBarCardsReturn {
  /** Prepends a newly-created AI card and opens the right bar to the cards tab. */
  const prependNewCard = useStableCallback((card: RightBarCard) => {
    setMergedState(getPrependedNewCardState(stateRef.current.newCards, card), () => {
      unCollapseRightBar();
    });
  });

  /** Records on the newCards wrapper that a card's stream finished or errored, so remounts never re-fire it. */
  const markCardStreamSettled = useStableCallback((cardModel: CardModel | null | undefined, error?: RightBarCardError) => {
    if (!cardModel) {
      return;
    }

    // Functional update: several cards can settle in the same tick, and a
    // stateRef snapshot would let the last one overwrite the others.
    setMergedState((previousState: RightBarCardsState) => {
      const wrapper = previousState.newCards.find((card) => card.model === cardModel);
      if (!wrapper || wrapper.doneStreaming) {
        return null;
      }

      const newCards = previousState.newCards.map((card) => {
        if (card !== wrapper) {
          return card;
        }
        if (error) {
          return { ...card, doneStreaming: true, loading: false, error };
        }
        return { ...card, doneStreaming: true, loading: false };
      });
      return { newCards };
    });
  });

  /** Clears loaded cards and reloads the first right-bar page for the active document. */
  const refreshCards = useStableCallback(() => {
    setMergedState({ existingCards: [], newCards: [] }, () => {
      getCards(stride);
    });
  });

  // Lifecycle helpers for the Draft / legacy ProseGenerator placeholder card
  // (SW-9212). These let GeneratePanel/ProseGenerator add a card to the right
  // rail at generation start, stream prose into it, link it to the real
  // Firestore record when the backend emits its id, and finalize/remove it on
  // completion or error without doing a destructive refresh that collapses
  // other cards.
  /** Creates the temporary Draft/GeneratePanel placeholder card shown while prose streams. */
  const addDraftPlaceholderCard = useStableCallback(({ prompt, comparisonMode, modelLabel }: DraftPlaceholderOptions = {}) => {
    const initialContent = comparisonMode ? ["", ""] : [""];
    const card = new CardModelConstructor(
      null,
      {
        source: "chapterGenerator",
        documentId: documentIdRef.current,
        prompt: prompt || "",
        content: initialContent,
        createdAt: new Date(),
        userId: getUserId(),
        // Mirror the chiclets the server writes onto the persisted card
        // (ChapterGenerator). The placeholder is what the user interacts with
        // until reload, and model-dependent card behavior (the Muse 2 alpha
        // note) keys off chiclets.model, so it must exist from creation.
        ...(modelLabel ? { chiclets: { model: modelLabel } } : {}),
      },
      false,
    );
    // loading:false keeps the card body visible from the moment it appears,
    // so prose streams in just like Write cards. We rely on the parent's
    // setState (replacing the wrapper object) for re-renders as content grows.
    const wrapped = {
      loading: false,
      doneStreaming: false,
      hidden: false,
      model: card,
      ready: false,
      params: { documentId: documentIdRef.current },
    };
    const newCards = [wrapped, ...stateRef.current.newCards];
    setMergedState({ newCards });
    return card;
  });

  /** Batches placeholder-card rerenders so frequent streaming chunks do not force one render per chunk. */
  const schedulePlaceholderRerender = useStableCallback((cardModel: CardModel) => {
    if (placeholderRafIdRef.current) return;
    placeholderRafIdRef.current = requestAnimationFrame(() => {
      placeholderRafIdRef.current = null;
      const idx = stateRef.current.newCards.findIndex((c) => c.model === cardModel);
      if (idx === -1) return;
      const next = [...stateRef.current.newCards];
      next[idx] = { ...next[idx], loading: false };
      setMergedState({ newCards: next });
    });
  });

  /** Appends a streamed text chunk into a placeholder card content slot. */
  const appendDraftPlaceholderContent = useStableCallback((cardModel: CardModel | null | undefined, text: string, { index = 0 }: DraftPlaceholderContentOptions = {}) => {
    if (!cardModel || !text) return;
    if (!Array.isArray(cardModel.data.content)) {
      cardModel.data.content = [""];
    }
    const content = cardModel.data.content as string[];
    while (content.length <= index) {
      content.push("");
    }
    content[index] = (content[index] || "") + text;
    // Mutation alone won't trigger re-render; schedule a single rAF-batched
    // setState so high-frequency chunks (e.g. comparison-mode onChunk fires
    // per-chunk, not debounced) coalesce into one render per frame.
    schedulePlaceholderRerender(cardModel);
  });

  /** Replaces the placeholder card's generated client id with the real persisted card id. */
  const linkDraftPlaceholderToCardId = useStableCallback((cardModel: CardModel | null | undefined, cardId: string | null | undefined) => {
    if (!cardModel || !cardId || cardModel.id === cardId) return;
    cardModel.setFetched(cardId);
    const idx = stateRef.current.newCards.findIndex((c) => c.model === cardModel);
    if (idx === -1) return;
    const next = [...stateRef.current.newCards];
    next[idx] = { ...next[idx] };
    setMergedState({ newCards: next });
  });

  /** Marks a placeholder card as finished so the right bar stops treating it as actively streaming. */
  const finalizeDraftPlaceholder = useStableCallback((cardModel: CardModel | null | undefined) => {
    if (!cardModel) return;
    const idx = stateRef.current.newCards.findIndex((c) => c.model === cardModel);
    if (idx === -1) return;
    const next = [...stateRef.current.newCards];
    next[idx] = { ...next[idx], doneStreaming: true, loading: false };
    setMergedState({ newCards: next, rightTab: 0 }, () => {
      unCollapseRightBar();
    });
  });

  /** Removes a placeholder card when generation is cancelled or fails before it should remain visible. */
  const removeDraftPlaceholderCard = useStableCallback((cardModel: CardModel | null | undefined) => {
    if (!cardModel) return;
    const idx = stateRef.current.newCards.findIndex((c) => c.model === cardModel);
    if (idx === -1) return;
    const next = [...stateRef.current.newCards.slice(0, idx), ...stateRef.current.newCards.slice(idx + 1)];
    setMergedState({ newCards: next });
  });

  // Gets a set of cards for the right bar, optionally given a startAfter and appends it to the list existing cards
  /** Loads a page of persisted cards for the current document, ignoring stale document-switch responses. */
  const getCards = useStableCallback(async (limit: number, startAfter?: CardPageCursor | null, switchVersion: number | null = null) => {
    const capturedVersion = switchVersion ?? documentSwitchVersionRef.current;

    let query = db
      .collection("cards")
      .where("userId", "==", getUserId())
      .where("documentId", "==", documentIdRef.current)
      .where("deleted", "==", false)
      .orderBy("createdAt", "desc")
      .limit(limit);

    if (startAfter) {
      if (shouldSkipCardPageRequest(startAfter, cardQueriesRef.current)) {
        return;
      } else {
        query = query.startAfter(startAfter);
      }
    }

    let snap = await query.get();

    if (!isCurrentDocumentSwitchVersion(documentSwitchVersionRef.current, capturedVersion)) return;

    markCardPageRequested(startAfter, cardQueriesRef.current);

    const cardData = createRightBarCardsFromSnapshot<CardModel>(snap as FirestoreCardSnapshot, CardModelConstructor);

    setMergedState(getLoadedExistingCardsState(stateRef.current.existingCards, cardData));
  });

  /** Removes or refreshes a matching card from one of the right-bar card lists. */
  const deleteCardOfType = useStableCallback((cardType: RightBarCardListKey, id: string, keepCard: boolean) => {
    // The card model is expected to handle deletion
    // and update card model data by now.
    const idx = stateRef.current[cardType].findIndex((card) => {
      return card.model.id === id;
    });

    if (idx === -1) {
      return;
    }

    const modifiedCard = stateRef.current[cardType][idx];

    // If the card is not completely deleted,
    // we trigger an update to the state with the modified card
    setMergedState({
      [cardType]: keepCard
        ? [...stateRef.current[cardType].slice(0, idx), modifiedCard, ...stateRef.current[cardType].slice(idx + 1)]
        : [...stateRef.current[cardType].slice(0, idx), ...stateRef.current[cardType].slice(idx + 1)],
    });
  });

  /** Applies card deletion to both generated and persisted right-bar lists. */
  const deleteCard = useStableCallback((id: string, keepCard: boolean = false) => {
    deleteCardOfType("newCards", id, keepCard);
    deleteCardOfType("existingCards", id, keepCard);
  });

  /** Adapts RightBarContainer's item-deletion callback into the card-list deletion semantics. */
  const deleteCardItem = useStableCallback((id: string, removeCard: boolean) => {
    deleteCard(id, !removeCard);
  });

  /** Creates a favorite from a card item and refreshes favorite-derived state. */
  const onCardFavorite = useStableCallback((card: CardModel, content: unknown) => {
    const favoritesStore = favoritesStoreRef.current;
    if (!favoritesStore) return;

    favoritesStore
      .createFromCard(card, content)
      .then(() => {
        if (favoritesStoreRef.current !== favoritesStore) return;
        setMergedState({
          favorites: favoritesStore.getSortedFavorites(),
          favoritesByCards: favoritesStore.calculateFavoritesByCards(),
        });
      })
      .catch((error) => {
        console.error("Error creating favorite:", error);
      });
  });

  /** Removes a card item's favorite and refreshes favorite-derived state. */
  const onCardUnfavorite = useStableCallback((card: CardModel, content: unknown) => {
    const favoritesStore = favoritesStoreRef.current;
    if (!favoritesStore) return;

    favoritesStore.deleteFromCard(card, content).then(() => {
      if (favoritesStoreRef.current !== favoritesStore) return;
      setMergedState({
        favorites: favoritesStore.getSortedFavorites(),
        favoritesByCards: favoritesStore.calculateFavoritesByCards(),
      });
    });
  });

  /** Deletes a favorite directly from the favorites tab and refreshes favorite-derived state. */
  const onDeleteFavorite = useStableCallback((id: string) => {
    const favoritesStore = favoritesStoreRef.current;
    if (!favoritesStore) return;

    favoritesStore.delete(id).then(() => {
      if (favoritesStoreRef.current !== favoritesStore) return;
      setMergedState({
        favorites: favoritesStore.getSortedFavorites(),
        favoritesByCards: favoritesStore.calculateFavoritesByCards(),
      });
    });
  });

  /** Persists a favorite reorder and mirrors the sorted favorite list into component state. */
  const onFavoriteReorder = useStableCallback((from: number, to: number) => {
    const favoritesStore = favoritesStoreRef.current;
    if (!favoritesStore) return;

    favoritesStore.reorder(from, to);
    setMergedState({ favorites: favoritesStore.getSortedFavorites() });
  });

  /** Changes the active right-bar tab only when the requested tab is different. */
  const onChangeTab = useStableCallback((tab: number) => {
    if (stateRef.current.rightTab !== tab) {
      setMergedState({ rightTab: tab });
    }
  });

  // Gets the next page of cards based on the state of the existing cards
  /** Requests the next persisted-card page using the last loaded card snapshot as the cursor. */
  const onNextPage = useStableCallback(() => {
    // get the last card
    let card = stateRef.current.existingCards[stateRef.current.existingCards.length - 1];
    if (card) {
      getCards(stride, card.snapshot);
    }
  });

  return {
    refreshCards,
    getCards,
    prependNewCard,
    markCardStreamSettled,
    deleteCard,
    deleteCardItem,
    onNextPage,
    onChangeTab,
    onCardFavorite,
    onCardUnfavorite,
    onDeleteFavorite,
    onFavoriteReorder,
    addDraftPlaceholderCard,
    appendDraftPlaceholderContent,
    linkDraftPlaceholderToCardId,
    finalizeDraftPlaceholder,
    removeDraftPlaceholderCard,
  };
}
